// Send all payment texts (docs/plans/send-all-texts/PLAN.md): the words of one text, the pure half. The phone builds
// them for the preview (Read) and the server builds them again from what it reads itself before anything goes
// (functions/lib/chase-send.ts), so the money, the bank lines, the review line and the STOP line are always the
// server's. The only word the phone gives is the job's name ("gutters"), as Owed already writes it.
//
// The messages are Owed's own: the payment message, then reminders 1 to 3 in the owner's wording (chase-message.ts).
// Someone owed for two or more visits gets one text for the total, in the words Roundly already uses for an account
// (Liam, 27 Oct 2026: one text for the total).

import { accountChaseMessage, chaseMessage, nextRung, paymentTodayMessage, type ChaseRung } from './chase-message'
import { PAYMENT_MESSAGE_DAYS } from './owed-view'
import type { BusinessSettings } from './business-settings'

export type SendKind = 'payment' | 'chase_1' | 'chase_2' | 'chase_3'
export const KIND_ORDER: SendKind[] = ['payment', 'chase_1', 'chase_2', 'chase_3']
export const KIND_WORDS: Record<SendKind, string> = { payment: 'Payment message', chase_1: 'Reminder 1', chase_2: 'Reminder 2', chase_3: 'Reminder 3' }
const RUNG_KIND: Record<ChaseRung, SendKind> = { lean: 'chase_1', full: 'chase_2', soft: 'chase_3' }
const KIND_RUNG: Record<SendKind, ChaseRung> = { payment: 'full', chase_1: 'lean', chase_2: 'full', chase_3: 'soft' }

export interface WordsVisit {
  date: string
  // What is still to pay on it (the price less credit and part payments), or null when it has no price.
  toCollectPence: number | null
  chaseCount: number
  // The job's word as Owed writes it ("gutters"): only for a customer with more than one job, else null.
  job?: string | null
}

export const daysBetween = (from: string, to: string): number => Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000)

// Which message a visit is due: the payment message when nothing has gone and it was done in the last week, else the
// next reminder on the ladder (a first one over 30 days old already uses reminder 2's fuller words).
export function kindOf(v: Pick<WordsVisit, 'date' | 'chaseCount'>, today: string): SendKind {
  const age = daysBetween(v.date, today)
  if (v.chaseCount === 0 && v.date <= today && age <= PAYMENT_MESSAGE_DAYS) return 'payment'
  return RUNG_KIND[nextRung(v.chaseCount, age)]
}

// One text for several visits goes at the rung of the one furthest along.
export function kindOfAll(visits: Pick<WordsVisit, 'date' | 'chaseCount'>[], today: string): SendKind {
  return visits.map((v) => kindOf(v, today)).reduce((a, b) => (KIND_ORDER.indexOf(b) > KIND_ORDER.indexOf(a) ? b : a), 'payment' as SendKind)
}

export interface WordsInput {
  name: string | null | undefined
  visits: WordsVisit[]
  today: string
  settings: BusinessSettings
  // The owner's "Add my review link to payment texts" (business_settings.reviews_in_texts), off as standard.
  reviewsInTexts: boolean
  // No text from the round's Roundly number has gone to this number before: the STOP line goes on the end.
  firstText: boolean
}

// The STOP line on the first text from Roundly to a number (Twilio's rule: say who it is from and how to stop them).
export const stopLine = (businessName: string): string => `${businessName.trim() || 'Us'}: reply STOP to stop these texts.`

// The review sentence, taken out (Liam, 27 Oct 2026: payment only, unless the owner switches the link on). Only a
// sentence that is about a review: one holding the saved reviews link, a Google review address, or the word review.
// Any other link, a payment link included, and the bank lines stay.
const REVIEW_HOSTS = /\b(maps\.app\.goo\.gl|g\.page|goo\.gl\/maps|g\.co\/kgs|search\.google\.com\/local|google\.[a-z.]+\/maps)\S*/i
export function withoutReview(text: string, link: string | null | undefined): string {
  const saved = (link ?? '').trim()
  const about = (sentence: string): boolean => /\breview/i.test(sentence) || REVIEW_HOSTS.test(sentence) || (!!saved && sentence.includes(saved)) || /\{reviews link\}/.test(sentence)
  const lines = text.split('\n').map((line) => {
    if (!about(line)) return line
    // Sentences end at . ! or ? followed by a space; a web address's own dots are never followed by one.
    const kept = line.split(/(?<=[.!?])\s+/).filter((s) => !about(s))
    return kept.length ? kept.join(' ') : null
  })
  return lines.filter((l): l is string => l !== null).join('\n').replace(/\n{3,}/g, '\n\n').replace(/\s+$/, '')
}

// The review line, when switched on and a link is saved: the thanks-for-paying words, just above the sign-off.
export function withReview(text: string, link: string, signoff: string): string {
  const line = `If you were happy with the clean, a Google review really helps us: ${link}`
  const lines = text.split('\n')
  let at = -1
  for (let i = lines.length - 1; i >= 0; i--) if (signoff.trim() && lines[i]!.trim() === signoff.trim()) { at = i; break }
  if (at === -1) return `${text}\n\n${line}`
  // Back over blank lines to a closing like "Many thanks," or "Kind regards," so the line sits above it.
  let j = at - 1
  while (j >= 0 && !lines[j]!.trim()) j--
  const from = j >= 0 && /,\s*$/.test(lines[j]!) ? j : at
  lines.splice(from, 0, ...(from > 0 && lines[from - 1]!.trim() !== '' ? ['', line, ''] : [line, '']))
  return lines.join('\n').replace(/\n{3,}/g, '\n\n')
}

export function sendAllWords(p: WordsInput): string {
  const s = p.settings
  const kind = kindOfAll(p.visits, p.today)
  let words: string
  if (p.visits.length === 1) {
    const v = p.visits[0]!
    words = kind === 'payment'
      ? paymentTodayMessage({ name: p.name, date: v.date, amountPence: v.toCollectPence, job: v.job?.trim() || 'windows' }, s)
      : chaseMessage({ name: p.name, date: v.date, amountPence: v.toCollectPence, job: v.job ?? null, today: p.today }, KIND_RUNG[kind], s)
  } else {
    const total = p.visits.reduce((n, v) => n + (v.toCollectPence ?? 0), 0)
    words = accountChaseMessage(p.name, total, KIND_RUNG[kind], s)
  }
  words = withoutReview(words, s.reviews_link)
  const link = s.reviews_link?.trim()
  if (p.reviewsInTexts && link) words = withReview(words, link, s.chase_signoff)
  if (p.firstText) words = `${words}\n\n${stopLine(s.business_name)}`
  return words
}

// A short fingerprint of the words, so the server can tell the owner read exactly what it is about to send.
export function wordsPrint(words: string): string {
  let h = 5381
  for (let i = 0; i < words.length; i++) h = ((h << 5) + h + words.charCodeAt(i)) >>> 0
  return `${words.length.toString(36)}-${h.toString(36)}`
}

// What comes next for a visit once this text has gone (B7), worked out from the counts after the send: the payment
// message counts as the first chase, so reminder 2 follows it a week on; a stale first reminder already used
// reminder 2's words, so reminder 2 comes again; after the third, it is a phone call.
export function nextAfter(kind: SendKind, countAfter: number): { kind: SendKind | 'call'; again: boolean } {
  if (countAfter >= 3) return { kind: 'call', again: false }
  const next = RUNG_KIND[nextRung(countAfter, 0)]
  return { kind: next, again: next === kind }
}
